<script setup>
import { computed, inject, onMounted, ref } from 'vue';
import { OVERVIEW_METRICS } from '../constants';
import { useToggle } from '@vueuse/core';

import MetricCard from './overview/MetricCard.vue';
import MetricStatCard from './overview/MetricStatCard.vue';
import { LIVE_METRICS, buildLiveMetric } from './overview/liveMetrics';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
const openOverviewDetail = inject('openOverviewDetail', null);

const uiFlags = useMapGetter('getOverviewUIFlags');
const agentStatus = useMapGetter('agents/getAgentStatus');
const agents = useMapGetter('agents/getAgents');
const accountConversationMetric = useMapGetter('getAccountConversationMetric');
const store = useStore();

const accounti18nKey = 'OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS';
const teams = useMapGetter('teams/getTeams');

const AGENT_STATUS_STYLES = {
  online: { icon: 'i-lucide-circle-check', tone: 'teal' },
  busy: { icon: 'i-lucide-circle-minus', tone: 'amber' },
  offline: { icon: 'i-lucide-circle-off', tone: 'slate' },
};

const teamMenuList = computed(() => {
  return [
    { label: t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.ALL_TEAMS'), value: null },
    ...teams.value.map(team => ({ label: team.name, value: team.id })),
  ];
});

const agentStatusMetrics = computed(() =>
  Object.keys(agentStatus.value).map(key => ({
    key,
    label: t(`OVERVIEW_REPORTS.AGENT_STATUS.${OVERVIEW_METRICS[key]}`),
    value: agentStatus.value[key],
    ...AGENT_STATUS_STYLES[key],
  }))
);
const conversationMetrics = computed(() =>
  Object.keys(LIVE_METRICS).map(key =>
    buildLiveMetric(t, key, accountConversationMetric.value[key])
  )
);

const openAgentStatus = metric => {
  const matchingAgents = agents.value
    .filter(agent => agent.availability_status === metric.key)
    .map(agent => ({
      id: agent.id,
      name: agent.available_name || agent.name,
      email: agent.email,
      thumbnail: agent.thumbnail,
      status: agent.availability_status,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  openOverviewDetail?.({
    title: metric.label,
    description: t('OVERVIEW_REPORTS.AGENT_STATUS.HEADER'),
    kind: 'agents',
    agents: matchingAgents,
  });
};

const selectedTeam = ref(null);
const openConversationMetric = metric => {
  openOverviewDetail?.({
    title: metric.label,
    description: t('OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS.HEADER'),
    kind: 'conversations',
    source: 'live',
    params: {
      metric: metric.key,
      team_id: selectedTeam.value,
    },
  });
};

const selectedTeamLabel = computed(() => {
  const team =
    teamMenuList.value.find(
      menuItem => menuItem.value === selectedTeam.value
    ) || {};
  return team.label;
});
const fetchData = () => {
  const params = {};
  if (selectedTeam.value) {
    params.team_id = selectedTeam.value;
  }
  store.dispatch('fetchAccountConversationMetric', params);
};

const { startRefetching } = useLiveRefresh(fetchData);
const [showDropdown, toggleDropdown] = useToggle();

const handleAction = ({ value }) => {
  toggleDropdown(false);
  selectedTeam.value = value;
  fetchData();
};

onMounted(() => {
  store.dispatch('agents/get');
  fetchData();
  startRefetching();
});
</script>

<template>
  <div class="grid grid-cols-1 gap-4 xl:grid-cols-2">
    <div class="flex min-w-0 conversation-metric">
      <MetricCard
        :header="t(`${accounti18nKey}.HEADER`)"
        :is-loading="uiFlags.isFetchingAccountConversationMetric"
        :loading-message="t(`${accounti18nKey}.LOADING_MESSAGE`)"
      >
        <template v-if="teams.length" #control>
          <div
            v-on-clickaway="() => toggleDropdown(false)"
            class="relative flex items-center group z-50"
          >
            <Button
              sm
              slate
              faded
              :label="selectedTeamLabel"
              class="capitalize rounded-md group-hover:bg-n-alpha-2"
              @click="toggleDropdown()"
            />
            <DropdownMenu
              v-if="showDropdown"
              :menu-items="teamMenuList"
              class="mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0 top-full"
              label-class="capitalize"
              @action="handleAction($event)"
            />
          </div>
        </template>
        <div class="grid w-full grid-cols-1 gap-3 sm:grid-cols-3">
          <MetricStatCard
            v-for="metric in conversationMetrics"
            :key="metric.key"
            :label="metric.label"
            :value="metric.value"
            :icon="metric.icon"
            :tone="metric.tone"
            :hint="metric.hint"
            @select="openConversationMetric(metric)"
          />
        </div>
      </MetricCard>
    </div>
    <div class="flex min-w-0">
      <MetricCard :header="$t('OVERVIEW_REPORTS.AGENT_STATUS.HEADER')">
        <div class="grid w-full grid-cols-1 gap-3 sm:grid-cols-3">
          <MetricStatCard
            v-for="metric in agentStatusMetrics"
            :key="metric.key"
            :label="metric.label"
            :value="metric.value"
            :icon="metric.icon"
            :tone="metric.tone"
            @select="openAgentStatus(metric)"
          />
        </div>
      </MetricCard>
    </div>
  </div>
</template>
